import React, {useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {useLogin} from '@/shared/hooks/useApi';
import {useAuth} from '@/modules/auth/hooks/useAuth';
import {useAppDispatch} from '@/shared/hooks/redux';
import {setCurrentPatient} from '@/slices/patientSlice';
import {ThemedLogo} from '@/shared/themed/ThemedLogo';
import ScrollingPeople from './ScrollingPeople';
import {PasswordInput} from './PasswordInput';
import {logError} from '@/shared/utils/logger';
import {isOrganizationUser, isProviderOrOrganizationUser, isUserAdmin} from '@/shared/utils/tokenUtils';
import AuthService from '@/modules/auth/services/authService';

const LoginPage: React.FC = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');
    const loginMutation = useLogin();
    const {login} = useAuth();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const handleResendVerification = async () => {
        setResendStatus('sending');
        try {
            await AuthService.resendVerificationEmail({email: username.trim()});
            setResendStatus('sent');
        } catch (error) {
            logError('Resend verification email failed', error);
            setResendStatus('failed');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            const response = await loginMutation.mutateAsync({username, password});

            // Set current patient from login response (like old implementation)
            if (response?.userInfo) {
                dispatch(
                    setCurrentPatient({
                        id: response.userInfo.id,
                        firstName: response.userInfo.firstName,
                        lastName: response.userInfo.lastName,
                        fullName: `${response.userInfo.firstName} ${response.userInfo.lastName}`,
                        birthdate: response.userInfo.birthday || '',
                    })
                );
            }

            // Set auth user - only called if mutation was successful
            login(response);

            // Navigate immediately to prevent flicker from AuthLayout redirect
            const token = response?.token;
            if (isUserAdmin(token)) {
                navigate('/admin', {replace: true});
            } else if (isOrganizationUser(token)) {
                navigate('/admin', {replace: true});
            } else if (isProviderOrOrganizationUser(token)) {
                navigate('/provider', {replace: true});
            } else {
                navigate('/dashboard', {replace: true});
            }
        } catch (error) {
            // Error is handled by React Query and displayed via loginMutation.error
            // Do not call login() - user should remain unauthenticated
            logError('Login failed', error);
        }
    };

    return (
        <div className="h-screen flex bg-mld-background overflow-hidden">
            {/* Left side - Login Form */}
            <div className="flex-1 flex items-center justify-center bg-mld-background px-4 sm:px-6 lg:px-20">
                <div className="max-w-sm w-full space-y-8">
                    {/* Logo */}
                    <div className="text-center">
                        <ThemedLogo className="mx-auto mb-12 max-h-[200px] max-w-[300px] h-auto w-auto object-contain" />
                    </div>

                    {/* Title and Subtitle */}
                    <div className="text-center">
                        <h2 className="text-3xl font-light text-mld-menu-text mb-2">Hello</h2>
                        <p className="text-mld-menu-text text-base">Please sign in to continue</p>
                    </div>

                    {/* Login Form */}
                    <form onSubmit={handleSubmit} className="mt-8 space-y-6">
                        <div className="space-y-4">
                            <div>
                                <input
                                    id="username"
                                    type="text"
                                    required
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-mld-primary outline-none transition-colors text-gray-700 placeholder-gray-500 bg-white"
                                    placeholder="Email"
                                />
                            </div>

                            <div>
                                <PasswordInput
                                    id="password"
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Password"
                                    autoComplete="current-password"
                                />
                            </div>
                        </div>

                        {/* Login Button */}
                        <button
                            type="submit"
                            disabled={loginMutation.isPending}
                            className="w-full bg-mld-login-button hover:bg-opacity-90 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                            {loginMutation.isPending ? 'Signing In...' : 'Login'}
                        </button>

                        {/* Error Message */}
                        {loginMutation.error && (
                            <div className="mt-4 p-3 bg-mld-danger/5 border border-mld-danger/20 rounded-lg">
                                <p className="text-mld-danger text-sm text-center">
                                    {loginMutation.error.message === 'User not verified'
                                        ? 'Please verify your email address before logging in. Check your inbox for a verification email.'
                                        : loginMutation.error.message || 'Login failed. Please try again.'}
                                </p>
                                {loginMutation.error.message === 'User not verified' && (
                                    <div className="mt-2 text-center">
                                        {resendStatus === 'sent' ? (
                                            <p className="text-mld-menu-text text-sm">
                                                Verification email sent — check your inbox.
                                            </p>
                                        ) : (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={handleResendVerification}
                                                    disabled={resendStatus === 'sending' || !username.trim()}
                                                    className="text-mld-muted-text hover:text-mld-login-button text-sm underline transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                                                    {resendStatus === 'sending'
                                                        ? 'Sending…'
                                                        : 'Resend verification email'}
                                                </button>
                                                {resendStatus === 'failed' && (
                                                    <p className="text-mld-danger text-sm mt-1">
                                                        Could not send the email — please try again or contact
                                                        support.
                                                    </p>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Forgot Password Link with divider below */}
                        <div className="text-center space-y-3">
                            <Link
                                to="/reset"
                                className="text-mld-muted-text hover:text-mld-login-button text-sm transition-colors">
                                Forgot your password?
                            </Link>
                            <div className="mx-auto w-16 h-0.5 bg-mld-login-button mt-2"></div>
                        </div>

                        {/* Sign Up Link */}
                        <div className="text-center">
                            <span className="text-mld-menu-text text-sm font-bold">Not a member yet? </span>
                            <Link
                                to="/register"
                                className="text-mld-muted-text hover:text-mld-login-button text-sm font-normal transition-colors">
                                Sign Up Here
                            </Link>
                        </div>
                    </form>
                </div>
            </div>

            {/* Right side - Scrolling People */}
            <div className="hidden lg:flex flex-1 bg-mld-background">
                <ScrollingPeople />
            </div>
        </div>
    );
};

export default LoginPage;
